import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Network, ShieldAlert, Flame, SearchCheck, ClipboardCheck, Lock, CheckCircle2 } from 'lucide-react';
import { Navbar } from '../../components/Navbar';
import { SEO, serviceSchema, faqSchema, breadcrumbSchema } from '../../components/SEO';
import { FAQSection } from './PillarPage';

const NETWORKING_THEME = { primary: '#DC2626', accent: '#EF4444', soft: '#FEF2F2' };

const NETWORKING_FAQS = [
  {
    question: 'Do you design and install networks for offices and multi-site businesses?',
    answer: 'Yes. We design and deploy LAN and WAN networks for offices of all sizes, including multi-site and branch connectivity. Every project starts with a site survey, includes documentation and handover, and is built for performance, resilience, and future growth.',
  },
  {
    question: 'How do you price network projects?',
    answer: 'We price after a site survey — not before. Network projects vary significantly based on building size, user count, redundancy requirements, security standards, and hardware choices. We scope everything after the survey and give you a fixed proposal so there are no surprises mid-project.',
  },
  {
    question: 'Which networking vendors and equipment do you work with?',
    answer: 'We work with Cisco, Meraki, Fortinet, Ubiquiti, Aruba, HP/Arista, and Palo Alto. We recommend hardware based on your scale, budget, and in-house skillset — not on vendor incentives. Our documentation and handover are platform-agnostic so your team can operate the network after we leave.',
  },
  {
    question: 'What does vulnerability management include?',
    answer: 'It includes scheduled vulnerability scanning across your IT environment, risk-based prioritization of findings, coordinated patch management, and ongoing reporting. The goal is to identify and remediate weaknesses before attackers exploit them.',
  },
  {
    question: 'How often should we run a vulnerability scan or penetration test?',
    answer: 'Vulnerability scans should run continuously or at minimum monthly — attackers do not wait for quarterly reviews. Penetration tests are typically conducted annually or after major infrastructure changes, new product launches, or compliance requirements. For PCI DSS, quarterly ASV scans and annual pen tests are mandatory.',
  },
  {
    question: 'Do you perform penetration testing?',
    answer: 'Yes. We conduct ethical penetration testing on networks, web applications, and APIs to identify exploitable vulnerabilities. Each engagement ends with a detailed report, prioritized remediation guidance, and a re-test option.',
  },
  {
    question: 'What is the difference between a vulnerability scan and a penetration test?',
    answer: 'A vulnerability scan is automated — it identifies known weaknesses across your systems using a scanner like Nessus or Qualys. A penetration test is manual — our engineers actively attempt to exploit vulnerabilities to determine real-world impact. Scans tell you what is exposed; pen tests tell you what an attacker can actually do with it.',
  },
  {
    question: 'Can you help us achieve compliance (ISO 27001, SOC 2, PCI DSS)?',
    answer: 'Yes. We help businesses prepare for and maintain compliance with ISO 27001, SOC 2, PCI DSS, and other security frameworks — covering policy design, control implementation, audit readiness, and ongoing evidence collection.',
  },
  {
    question: 'Do you manage firewalls and zero-trust security?',
    answer: 'Yes. We deploy and manage enterprise firewalls (Fortinet, Sophos, Palo Alto), implement zero-trust network access (ZTNA), VPN, identity-based access controls, and continuous monitoring across your network perimeter.',
  },
  {
    question: 'What is zero-trust and do we need it?',
    answer: 'Zero-trust is a security model that treats every connection — internal or external — as untrusted until verified. Instead of a hard perimeter, access is granted per-user, per-device, per-application based on identity and context. If your team is remote or hybrid, you use SaaS applications, or you have had a breach or phishing incident, zero-trust is worth implementing. We deploy ZTNA using tools like Microsoft Entra ID, Zscaler, and Cloudflare Access.',
  },
  {
    question: 'Can you respond to a live security incident?',
    answer: 'Yes. We provide incident response including containment, forensic analysis, evidence preservation, stakeholder communication support, and post-incident hardening. If you suspect an active breach, contact us immediately at hello@smartsolution.ai — we prioritize incident response above all other work.',
  },
  {
    question: 'Do you offer ongoing network monitoring and management?',
    answer: 'Yes. We provide 24/7 network monitoring, proactive fault detection, firmware and security patch management, and monthly health reports as a managed service. This is the right model if you do not have in-house network engineering capacity but need enterprise-grade uptime and security posture.',
  },
];

const services = [
  {
    id: '01',
    icon: Network,
    title: 'Network Design & Implementation',
    description:
      'End-to-end network planning and deployment tailored to your business scale — from small office setups to enterprise-grade multi-site topologies. We design for performance, resilience, and future growth.',
    highlights: [
      'Site surveys & topology planning',
      'LAN/WAN architecture design',
      'Multi-site & branch connectivity',
      'Network documentation & handover',
    ],
  },
  {
    id: '02',
    icon: ShieldAlert,
    title: 'Vulnerability Management',
    description:
      'Continuous assessment and prioritized remediation of security vulnerabilities across your entire IT environment. We identify weaknesses before attackers do — and help you fix them fast.',
    highlights: [
      'Scheduled vulnerability scanning',
      'Risk-based prioritization & reporting',
      'Patch management coordination',
      'Remediation verification',
    ],
  },
  {
    id: '03',
    icon: Flame,
    title: 'Perimeter Defense — Next-Gen Firewalls',
    description:
      'Next-Generation Firewall (NGFW) solutions from industry leaders Fortinet, Palo Alto Networks, and Cisco — designed, deployed, and managed to protect your perimeter against sophisticated modern threats.',
    highlights: [
      'Fortinet FortiGate configuration & management',
      'Palo Alto Networks NGFW deployment',
      'Cisco Firepower implementation',
      'IPS/IDS tuning & policy management',
    ],
  },
  {
    id: '04',
    icon: SearchCheck,
    title: 'Threat Detection & Response',
    description:
      'Proactive monitoring, real-time threat detection, and rapid incident response. We contain and neutralize cyber threats before they cause business disruption — with clear escalation procedures and SLA-backed response times.',
    highlights: [
      '24/7 security event monitoring',
      'SIEM implementation & tuning',
      'Incident response planning',
      'Threat hunting & forensic analysis',
    ],
  },
  {
    id: '05',
    icon: ClipboardCheck,
    title: 'Security Audits & Compliance',
    description:
      'Comprehensive security audits, risk assessments, and compliance reviews to ensure your infrastructure meets regulatory requirements and industry standards. We deliver clear findings with actionable remediation plans.',
    highlights: [
      'Infrastructure security audits',
      'Penetration testing & red team exercises',
      'Compliance gap analysis (ISO 27001, NIST, GDPR)',
      'Audit-ready reporting & evidence packs',
    ],
  },
  {
    id: '06',
    icon: Lock,
    title: 'Zero Trust Architecture',
    description:
      'Design and implementation of Zero Trust security frameworks that verify every user, device, and connection — eliminating implicit trust across your network and significantly reducing your attack surface.',
    highlights: [
      'Identity & access management (IAM)',
      'Micro-segmentation & least-privilege access',
      'Device posture & compliance enforcement',
      'Zero Trust maturity assessment & roadmap',
    ],
  },
];

const vendors = ['Fortinet', 'Palo Alto Networks', 'Cisco', 'Microsoft Defender', 'VMware NSX'];

export const NetworkingServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title="Networking, Cybersecurity & Vulnerability Management"
        description="Network design, vulnerability management, penetration testing, firewall and zero-trust security, and compliance support (ISO 27001, SOC 2, PCI DSS) for growing businesses."
        path="/services/networking"
        jsonLd={[
          serviceSchema({
            name: 'Networking & Cybersecurity Services',
            description: 'Network design and implementation, vulnerability management, penetration testing, firewall and zero-trust security, and compliance support.',
            path: '/services/networking',
          }),
          faqSchema(NETWORKING_FAQS),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/#services' },
            { name: 'Networking & Cybersecurity', path: '/services/networking' },
          ]),
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white border-b border-[#E2E8F0]">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full bg-[#3B82F6]/5 blur-[130px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#DC2626]/4 blur-[110px] pointer-events-none" />
        <div className="container-custom py-20 lg:py-28 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label" style={{ color: '#3B82F6' }}>Cybersecurity & Networking</span>
              <span className="w-6 h-px bg-[#3B82F6]/30" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-6 leading-tight"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}
            >
              Secure Networks,<br />
              <span
                className="italic"
                style={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Resilient Infrastructure
              </span>
            </h1>
            <p className="text-[#374151] text-lg max-w-2xl leading-relaxed mb-8">
              We build secure, high-performance network infrastructures while protecting your business from evolving
              cyber threats — from perimeter defense with industry-leading NGFWs to Zero Trust architecture implementation.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#3B82F6]/25 text-[#2563EB] bg-[#eef0eb] text-xs font-mono tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />
                Enterprise Grade
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#DC2626]/25 text-[#991B1B] bg-[#FEF2F2] text-xs font-mono tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]" />
                Fortinet · Palo Alto · Cisco
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#3B82F6]/25 text-[#2563EB] bg-[#eef0eb] text-xs font-mono tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />
                Zero Trust Ready
              </span>
            </div>
            <Link
              to="/book"
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-[#3B82F6] text-white text-sm font-semibold hover:bg-[#1f3a51] transition-colors duration-300 shadow-sm"
            >
              Get Free Security Assessment
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-[#F6F9FC]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="section-label" style={{ color: '#3B82F6' }}>What We Offer</span>
              <span className="w-6 h-px bg-[#3B82F6]/30" />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
            >
              Full-Spectrum Cybersecurity Services
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0]">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  className="group bg-white p-8 hover:bg-[#eef0eb] transition-colors duration-300 relative overflow-hidden"
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-[2px] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                    style={{ background: 'linear-gradient(90deg, #3B82F6, #2563EB)' }}
                  />
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-10 h-10 flex items-center justify-center border border-[#c5cac3] bg-[#eef0eb]">
                      <Icon size={18} style={{ color: '#3B82F6' }} />
                    </div>
                    <span className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF]">{service.id}</span>
                  </div>
                  <h3 className="text-[#0F172A] font-semibold mb-3 group-hover:text-[#3B82F6] transition-colors text-sm">
                    {service.title}
                  </h3>
                  <p className="text-[#374151] text-sm leading-relaxed mb-5">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-xs text-[#374151]">
                        <CheckCircle2 size={12} className="shrink-0 mt-0.5" style={{ color: '#3B82F6' }} />
                        {h}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          {/* Vendor / Trust strip */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-0 bg-[#eef0eb] border border-[#E2E8F0] border-t-0 flex flex-col sm:flex-row items-center justify-between gap-5 px-8 py-5"
          >
            <div>
              <p className="text-sm text-[#374151] font-medium">
                Serving businesses across <strong className="text-[#0F172A]">Kuwait</strong> and the{' '}
                <strong className="text-[#0F172A]">GCC region</strong>
              </p>
              <p className="text-xs text-[#9CA3AF] mt-0.5">Enterprise-grade solutions, local expertise</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {vendors.map((v) => (
                <span
                  key={v}
                  className="px-3 py-1 text-[0.6rem] font-mono tracking-widest uppercase border border-[#c5cac3] text-[#3B82F6] bg-white"
                >
                  {v}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why cyber matters section */}
      <section className="bg-white border-t border-b border-[#E2E8F0] py-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="section-label" style={{ color: '#3B82F6' }}>Our Approach</span>
                <span className="w-6 h-px bg-[#3B82F6]/30" />
              </div>
              <h2
                className="font-display font-bold text-[#0F172A] mb-5"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)' }}
              >
                Security-First at Every Layer
              </h2>
              <p className="text-[#374151] leading-relaxed text-sm mb-6">
                Cyber threats don't discriminate by company size. A single breach can cause irreparable damage to
                operations, reputation, and customer trust. We take a proactive, layered approach — building security
                into your network from the ground up rather than bolting it on after the fact.
              </p>
              <p className="text-[#374151] leading-relaxed text-sm">
                Our engineers bring hands-on experience with the industry's leading security platforms, and we
                work to ensure that your defenses are always ahead of the threat landscape.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="grid grid-cols-2 gap-px bg-[#E2E8F0]">
                {[
                  { label: 'Threat Prevention', desc: 'Block attacks before they enter your network.' },
                  { label: 'Rapid Detection', desc: 'Identify breaches in minutes, not months.' },
                  { label: 'Swift Response', desc: 'Contain and remediate threats immediately.' },
                  { label: 'Continuous Hardening', desc: 'Evolve defenses as threats evolve.' },
                ].map((item, i) => (
                  <div key={item.label} className="bg-white p-6">
                    <div className="w-1 h-5 mb-4" style={{ backgroundColor: '#3B82F6' }} />
                    <h4 className="font-semibold text-[#0F172A] text-sm mb-2">{item.label}</h4>
                    <p className="text-[#374151] text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={NETWORKING_FAQS} theme={NETWORKING_THEME} title="Networking & Cybersecurity FAQs" />

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#2563EB]">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#3B82F6]/12 blur-[120px] pointer-events-none" />
        <div className="container-custom py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="font-display font-bold text-white mb-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              Secure Your Business{' '}
              <span
                className="italic"
                style={{
                  background: 'linear-gradient(135deg, #2563EB 0%, #8da5b8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Before It's Too Late
              </span>
            </h2>
            <p className="text-[#94A3B8] max-w-xl mb-8 leading-relaxed">
              Get a free security assessment and discover where your network is most vulnerable.
              No commitment — just clarity on where you stand.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/book"
                className="group inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#0F172A] text-sm font-semibold hover:bg-[#F6F9FC] transition-colors duration-300"
              >
                Free Security Assessment
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white text-sm font-medium hover:border-white/40 hover:bg-white/5 transition-all duration-300"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer strip */}
      <div className="bg-[#0F172A] border-t border-[#1E40AF] py-6">
        <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono text-[0.55rem] tracking-widest text-[#5B7299] uppercase">
            © {new Date().getFullYear()} Smart Solutions · All Rights Reserved
          </p>
          <Link to="/" className="font-mono text-[0.55rem] tracking-widest text-[#5B7299] hover:text-[#94A3B8] uppercase transition-colors">
            ← Back to main site
          </Link>
        </div>
      </div>

    </div>
  );
};
